// Registry Central — the cross-cutting Registry Proposal hub (DCIC-16).
// One place to propose any new master entity (market, facility, actor,
// cooperative, conflict committee, transhumance route), singly or by Excel
// bulk upload. Status and Date Created are always system-set:
//   • proposals from other roles' module forms ("+ Propose") → Pending
//     Validation; R01 views them here and validates or rejects.
//   • types with a full registry form in their module (Market → Market
//     Registry) go to Pending Completion when R01 creates or validates them:
//     only name / type / state / LGA / ward are captured here, the module form
//     captures the rest, and completing it makes the entry Active.
//   • other types are Active as soon as R01 creates or validates them.
// Entries can be deactivated, archived and restored.
//
// There is no backend endpoint for this yet, so entries are kept in browser
// storage (same approach as the other frontend-only stores).
import { publishCollectionChange } from '../utils/collectionSync';

const COLLECTION_KEY = 'registry-central';
const STORAGE_KEY = 'nlims:registry-central';

export const REGISTRY_TYPE_OPTIONS = [
  { value: 'market', label: 'Market' },
  { value: 'facility', label: 'Facility' },
  { value: 'livestock_actor', label: 'Livestock Actors' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'conflict_committee', label: 'Conflict Committee' },
  { value: 'transhumance_route', label: 'Transhumance Route' },
];

export const REGISTRY_STATUS_OPTIONS = [
  { value: 'pending_validation', label: 'Pending Validation' },
  { value: 'pending_completion', label: 'Pending Completion' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

// Starting data for Registry Central (the agreed sample set). Applied once
// per SEED_VERSION: it replaces whatever is stored, so earlier test entries
// are cleared; entries created after that are kept. Bump SEED_VERSION to
// reset the table to this list again.
const SEED_VERSION = '2026-10-09';
const SEED_FLAG_KEY = `${STORAGE_KEY}:seed`;
const SEED_ROWS = [
  ['Sabini Market', 'market', 'Edo', 'active'],
  ['Chevening Vets Home', 'facility', 'Benue', 'pending_validation'],
  ['Samuel Adewale', 'livestock_actor', 'Edo', 'archived'],
  ['Dispute Resolvers', 'conflict_committee', 'Benue', 'inactive'],
  ['Benue–Benin', 'transhumance_route', 'Edo', 'active'],
  ['Dailylog', 'cooperative', 'Benue', 'active'],
  ['Sabini Market', 'market', 'Edo', 'active'],
  ['Chevening Vets Home', 'facility', 'Benue', 'active'],
  ['Samuel Adewale', 'livestock_actor', 'Edo', 'active'],
  ['Dispute Resolvers', 'conflict_committee', 'Benue', 'active'],
  ['Benue–Benin', 'transhumance_route', 'Edo', 'active'],
  ['Dailylog', 'cooperative', 'Benue', 'active'],
  ['Sabini Market', 'market', 'Edo', 'active'],
  ['Chevening Vets Home', 'facility', 'Benue', 'active'],
  ['Samuel Adewale', 'livestock_actor', 'Edo', 'active'],
  ['Dispute Resolvers', 'conflict_committee', 'Benue', 'archived'],
  ['Benue–Benin', 'transhumance_route', 'Edo', 'archived'],
  ['Dailylog', 'cooperative', 'Benue', 'active'],
  ['Sabini Market', 'market', 'Edo', 'active'],
  ['Chevening Vets Home', 'facility', 'Benue', 'pending_validation'],
];

function seedEntries() {
  const base = Date.parse('2026-10-09T12:00:00');
  return SEED_ROWS.map(([name, type, state, status], i) => {
    // newest first, so the table keeps the sample order under Date Created ↓
    const stamp = new Date(base - i * 60000).toISOString();
    return {
      uuid: `seed-${String(i + 1).padStart(2, '0')}`,
      registry_type: type, name, state, lga: 'Ogbini', ward: '', classification: '', notes: '',
      status, source: 'manual', batch_name: '', created_by: 'System', created_at: stamp, updated_at: stamp,
    };
  });
}

function readAll() {
  try {
    if (window.localStorage.getItem(SEED_FLAG_KEY) !== SEED_VERSION) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seedEntries()));
      window.localStorage.setItem(SEED_FLAG_KEY, SEED_VERSION);
    }
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    // "Draft" was renamed "Pending Validation".
    return Array.isArray(parsed) ? parsed.map((row) => (row.status === 'draft' ? { ...row, status: 'pending_validation' } : row)) : [];
  } catch {
    return [];
  }
}

function writeAll(rows) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  } catch {
    throw new Error('Could not save to browser storage — it may be full.');
  }
}

function newUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `reg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const text = (value) => String(value ?? '').trim();

// Registry types whose master record is completed in its own module form.
export const COMPLETION_TYPES = ['market'];
const validatedStatus = (type) => (COMPLETION_TYPES.includes(type) ? 'pending_completion' : 'active');
// R01's own entries skip validation; everyone else's await it.
const initialStatus = (user, type) => (user?.roleId === 'R01' ? validatedStatus(type) : 'pending_validation');

function buildEntry(payload, user, extra = {}) {
  const now = new Date().toISOString();
  return {
    uuid: newUuid(),
    registry_type: text(payload.registry_type),
    name: text(payload.name),
    state: text(payload.state),
    lga: text(payload.lga),
    ward: text(payload.ward),
    // Market proposals also carry the market's own details, which seed the
    // Market Registry form when the entry is completed.
    market_type: text(payload.market_type),
    market_status: text(payload.market_status),
    market_schedule: text(payload.market_schedule),
    status: initialStatus(user, text(payload.registry_type)),
    linked_record_uuid: '',
    rejection_notes: '',
    source: 'manual',
    batch_name: '',
    created_by: user?.name ?? '',
    created_by_role: user?.roleId ?? '',
    created_at: now,
    updated_at: now,
    ...extra,
  };
}

export const registryCentralApi = {
  collectionKey: COLLECTION_KEY,

  async listAll() {
    const items = readAll().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
    return { items };
  },

  async create(payload, user) {
    const entry = buildEntry(payload, user);
    writeAll([entry, ...readAll()]);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: entry.uuid });
    return entry;
  },

  // Excel bulk upload: one entry per row (each row carries its own Registry
  // Type), tagged with the batch name.
  async createMany(rows, { batchName }, user) {
    const entries = rows.map((row) => buildEntry(row, user, {
      source: 'bulk_upload',
      batch_name: text(batchName),
    }));
    writeAll([...entries, ...readAll()]);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', count: entries.length });
    return entries;
  },

  async update(uuid, payload) {
    const rows = readAll();
    const index = rows.findIndex((row) => row.uuid === uuid);
    if (index === -1) throw new Error('Registry entry not found.');
    const editable = ['registry_type', 'name', 'state', 'lga', 'ward'];
    const patch = Object.fromEntries(editable.filter((key) => key in payload).map((key) => [key, text(payload[key])]));
    rows[index] = { ...rows[index], ...patch, updated_at: new Date().toISOString() };
    writeAll(rows);
    publishCollectionChange(COLLECTION_KEY, { action: 'update', uuid });
    return rows[index];
  },

  // "+ Propose" from a module form (e.g. Market Price → new market).
  async propose(payload, user) {
    const entry = buildEntry(payload, user, { source: 'form_proposal' });
    writeAll([entry, ...readAll()]);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: entry.uuid });
    return entry;
  },

  async validate(uuid) {
    const row = readAll().find((r) => r.uuid === uuid);
    if (!row) throw new Error('Registry entry not found.');
    return this.setStatus(uuid, validatedStatus(row.registry_type), { rejection_notes: '' });
  },

  async reject(uuid, notes = '') {
    return this.setStatus(uuid, 'rejected', { rejection_notes: text(notes) });
  },

  // The module form saved the full master record — link it and go Active.
  async complete(uuid, linkedRecordUuid) {
    return this.setStatus(uuid, 'active', { linked_record_uuid: text(linkedRecordUuid) });
  },

  async listPendingCompletion(registryType) {
    return readAll().filter((row) => row.status === 'pending_completion' && row.registry_type === registryType);
  },

  async setStatus(uuid, status, extra = {}) {
    const rows = readAll();
    const index = rows.findIndex((row) => row.uuid === uuid);
    if (index === -1) throw new Error('Registry entry not found.');
    rows[index] = { ...rows[index], ...extra, status, updated_at: new Date().toISOString() };
    writeAll(rows);
    publishCollectionChange(COLLECTION_KEY, { action: 'status', uuid, status });
    return rows[index];
  },
};

// Called by the module forms' "+ Propose" pop-ups after they create a new
// master record, so every proposal is also listed in Registry Central.
// Never blocks the proposal itself.
export function logRegistryProposal(registryType, { name, state, lga, ward } = {}, user) {
  try {
    const entry = buildEntry({ registry_type: registryType, name, state, lga, ward }, user, { source: 'form_proposal' });
    writeAll([entry, ...readAll()]);
    publishCollectionChange(COLLECTION_KEY, { action: 'create', uuid: entry.uuid });
  } catch {
    // Registry Central is a register of proposals; failing to log must not fail the form.
  }
}

// Accepts a type value or label from a spreadsheet cell ("Livestock Actors",
// "livestock_actor", "Conflict Committee" …).
export function matchRegistryType(value) {
  const key = String(value ?? '').trim().toLowerCase().replace(/[^a-z]+/g, ' ').trim();
  if (!key) return '';
  const hit = REGISTRY_TYPE_OPTIONS.find((o) => o.value.replace(/_/g, ' ') === key || o.label.toLowerCase() === key
    || o.label.toLowerCase().replace(/s$/, '') === key.replace(/s$/, ''));
  return hit?.value ?? null;
}

export function registryTypeLabel(value) {
  return REGISTRY_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '';
}

export function registryStatusLabel(value) {
  return REGISTRY_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '';
}
